import React, { useState, useEffect } from 'react';
import CustomLink from './CustomLink';
import MothersDayGif from '../assets/mothers-day.webp';
import CopyToClipboard from '../assets/CopyToClipboard.webp';
import Unique from './Home/Unique';
import Preview8 from '../assets/8-preview.webp';
import { getBrowserLocale } from '../utils/language';
import {
  BoomTestimonial8Name,
  BoomTestimonial8Review,
  CustomerReviews,
  FounderAttribution,
  FounderQuote,
  HeroSocialProof,
  LisaSectionEyebrow,
  LisaSectionHeading,
  MysteryApplyCode,
  MysteryExpiredBadge,
  MysteryExpiredMessage,
  MysteryHeading,
  MysterySubHeading,
  RefillBtnText,
} from '../Constants';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Icon } from './elements/icon';
import ReviewsMobile from './Customer/components/ReviewsMobile';

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return count;
}

const DEFAULT_EXPIRY_HOURS = 24;

const ClockIcon = ({ className = 't-w-4 t-h-4 t-shrink-0' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

function useCountdown(quizId, expiryHours) {
  const durationMs = (expiryHours > 0 ? expiryHours : DEFAULT_EXPIRY_HOURS) * 3600000;
  const [timeLeft, setTimeLeft] = useState(durationMs);

  useEffect(() => {
    const key = `mysteryCredit_expiresAt_${quizId || 'default'}`;
    let expiresAt = parseInt(localStorage.getItem(key), 10);
    if (!expiresAt || Date.now() >= expiresAt) {
      // First visit — use the expiry from the URL param
      expiresAt = Date.now() + durationMs;
      localStorage.setItem(key, expiresAt);
    }
    // Returning visitors: expiresAt already stored, ignore current URL param
    const update = () => setTimeLeft(Math.max(0, expiresAt - Date.now()));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [quizId]);

  const h = Math.floor(timeLeft / 3600000);
  const m = Math.floor((timeLeft % 3600000) / 60000);
  const s = Math.floor((timeLeft % 60000) / 1000);
  return { h, m, s, expired: timeLeft === 0 };
}

const MysteryCredit = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get('quizId');
  const discountCode = searchParams.get('discountCode');
  const quizParams = new URLSearchParams();
  if (quizId) quizParams.set('quizId', quizId);
  if (discountCode) quizParams.set('discountCode', discountCode);
  const quizLink = quizParams.toString() ? `/quiz?${quizParams.toString()}` : '/quiz';
  const expiryHours = parseInt(searchParams.get('expiry'), 10);
  const dosesCount = useCountUp(300000, 1800);
  const formattedDoses = dosesCount.toLocaleString(getBrowserLocale(i18n.language));
  const { h, m, s, expired } = useCountdown(quizId, expiryHours);

  const [copied, setCopied] = useState(false);
  return (
    <>
      <div className="t-bg-white t-w-full t-p-8">
        <div className="t-w-full t-max-w-[1330px] t-mx-auto t-flex t-flex-col t-items-center t-justify-between md:t-flex-row ">
          <div className="t-max-w-[500px] t-h-full t-flex t-flex-col t-gap-2 md:t-gap-4 t-p-2 md:t-p-8">
            <div className="t-flex t-flex-col t-items-center md:t-items-start t-gap-4">
              <h1 className="t-text-center md:t-text-left !t-m-0 t-font-serif t-font-normal t-text-navy t-text-[36px] md:t-text-[42px]">
                {t(MysteryHeading)}
              </h1>
              <p className="t-text-center md:t-text-left !t-m-0 t-font-sans t-text-navy t-text-[18px] md:t-text-[20px]">
                {t(MysterySubHeading)}
              </p>
            </div>
            <div className="t-flex t-flex-col t-items-center md:t-items-start t-justify-center md:t-justify-start t-gap-2 md:t-gap-4 t-mt-4">
              <div className="t-flex t-items-center t-gap-2 t-bg-butter/20 t-border t-border-butter t-rounded-lg t-px-4 t-py-2 t-w-full t-justify-center md:t-justify-start">
                <ClockIcon />
                <span className="t-font-sans t-font-semibold t-text-navy t-text-sm t-tabular-nums">
                  {expired
                    ? t(MysteryExpiredBadge)
                    : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}
                </span>
              </div>
              <style>{`
                @keyframes mutterPulse {
                  0%, 100% { box-shadow: 0 0 0 0 rgba(246, 225, 71, 0.5); }
                  50% { box-shadow: 0 0 0 8px rgba(246, 225, 71, 0); }
                }
                .mutter-cta {
                  animation: mutterPulse 2s ease-in-out infinite;
                }
                .mutter-cta:active { transform: scale(0.97); }
              `}</style>
              {expired ? (
                <div className="t-w-full t-bg-red-50 t-border t-border-red-200 t-rounded-lg t-py-3 t-px-4 t-text-center">
                  <p className="!t-m-0 t-font-sans t-font-semibold t-text-red-500 t-text-base">
                    {t(MysteryExpiredMessage)}
                  </p>
                </div>
              ) : (
                <>
                  <p className="!t-m-0 t-font-sans t-text-navy/70 t-text-base t-text-center">
                    {t(MysteryApplyCode)}
                  </p>
                  <div
                    className="t-w-full t-bg-g100 t-border t-border-g200 t-flex t-items-center t-justify-between t-rounded-lg t-cursor-pointer"
                    onClick={() => {
                      navigator.clipboard.writeText('GUTHABEN');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 5000);
                    }}
                  >
                    <p className="t-w-[85%] !t-m-0 t-font-sans t-font-bold t-text-lg t-text-center t-text-navy t-tracking-widest">
                      GUTHABEN
                    </p>
                    <div className="t-h-full t-flex t-items-center t-gap-2 t-bg-g200 t-py-2 t-px-4 t-rounded-[0_8px_8px_0]">
                      {copied ? (
                        <Icon name="tick" className="t-bg-green-500 t-rounded-full t-p-[2px]" />
                      ) : (
                        <img src={CopyToClipboard} alt="" className="t-h-6 t-w-6" />
                      )}
                      <p className="!t-m-0 t-text-lg t-font-sans !t-font-bold t-text-navy">
                        {copied ? 'Copied' : 'Copy'}
                      </p>
                    </div>
                  </div>
                </>
              )}
              <CustomLink to={quizLink} className="t-w-full t-mt-4">
                <span className="mutter-cta btn !t-rounded-lg !t-w-full t-font-sans !t-bg-butter !t-text-navy !t-font-bold t-transition-transform t-inline-flex t-items-center t-justify-center t-gap-2">
                  {t(RefillBtnText)} →
                </span>
              </CustomLink>
              <div className="t-flex t-flex-col t-items-center t-gap-1 t-text-navy t-font-sans t-text-sm t-mt-2 t-w-full">
                <span className="t-text-butter t-text-lg t-tracking-tight">★★★★★</span>
                <span className="t-font-semibold">
                  {t(HeroSocialProof, { rating: '4.89', doses: formattedDoses })}
                </span>
              </div>
            </div>
          </div>
          <img src={MothersDayGif} alt="" className="t-w-full md:t-w-2/5 t-rounded-2xl t-mt-4 md:t-mt-0" />
        </div>
      </div>
      <div className="t-bg-navy t-w-full t-py-12 t-px-8">
        <div className="t-w-full t-max-w-[720px] t-mx-auto t-flex t-flex-col t-items-center t-gap-6">
          <span className="t-font-serif t-text-butter t-text-[64px] t-leading-none t-self-start">"</span>
          <p className="t-font-serif t-italic t-text-white t-text-[20px] md:t-text-[24px] t-leading-relaxed !t-m-0 -t-mt-8 t-text-center">
            {t(FounderQuote)}
          </p>
          <p className="t-font-sans t-text-butter t-text-[14px] t-tracking-wide t-uppercase !t-m-0">
            {t(FounderAttribution)}
          </p>
        </div>
      </div>
      <div id="lisa" className="t-bg-surface t-w-full t-py-12 t-px-6">
        <div className="t-w-full t-max-w-[900px] t-mx-auto t-flex t-flex-col md:t-flex-row t-items-center t-gap-8 md:t-gap-12">
          <div className="t-flex t-flex-col t-gap-4 t-items-center md:t-items-start">
            <p className="t-font-sans t-text-quiz-teal t-text-sm t-font-semibold t-uppercase t-tracking-widest !t-m-0">
              {t(LisaSectionEyebrow)}
            </p>
            <h2 className="t-font-serif t-font-normal t-text-navy t-text-[26px] md:t-text-[32px] t-text-center md:t-text-left !t-m-0">
              {t(LisaSectionHeading)}
            </h2>
            <p className="t-font-sans t-italic t-text-navy/80 t-text-[18px] t-leading-relaxed !t-m-0 t-text-center md:t-text-left">
              {t(BoomTestimonial8Review)}
            </p>
            <p className="t-font-sans t-text-sm t-font-semibold t-text-navy/50 !t-m-0">
              — {t(BoomTestimonial8Name)}
            </p>
          </div>
          <video
            controls
            className="t-w-full md:t-w-[340px] t-flex-none t-object-cover t-rounded-2xl t-border t-border-g200"
            style={{ aspectRatio: '9/16', maxHeight: '520px' }}
            poster={Preview8}
            playsInline
          >
            <source
              src={`${process.env.REACT_APP_PUBLIC_URL}resources/videos/Testimonials+/8.mp4`}
              type="video/mp4"
            />
          </video>
        </div>
      </div>
      <div className="t-max-w-[100vw] t-flex t-items-center t-justify-center">
        <Unique />
      </div>
      <div className="t-bg-navy t-w-full t-py-12 t-px-8">
        <div className="t-w-full t-max-w-[480px] t-mx-auto t-flex t-flex-col t-items-center t-gap-4">
          <h2 className="t-font-serif t-font-normal t-text-white t-text-[28px] md:t-text-[34px] t-text-center !t-m-0">
            {t(MysteryHeading)}
          </h2>
          <p className="t-font-sans t-text-white/70 t-text-base t-text-center !t-m-0">
            {t(MysterySubHeading)}
          </p>
          <div className="t-flex t-items-center t-gap-2 t-bg-white/10 t-border t-border-butter/50 t-rounded-lg t-px-4 t-py-2 t-w-full t-justify-center">
            <ClockIcon className="t-w-4 t-h-4 t-shrink-0 t-text-butter" />
            <span className="t-font-sans t-font-semibold t-text-butter t-text-sm t-tabular-nums">
              {expired
                ? t(MysteryExpiredBadge)
                : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}
            </span>
          </div>
          {expired ? (
            <div className="t-w-full t-bg-white/10 t-border t-border-red-400/50 t-rounded-lg t-py-3 t-px-4 t-text-center">
              <p className="!t-m-0 t-font-sans t-font-semibold t-text-red-400 t-text-base">
                {t(MysteryExpiredMessage)}
              </p>
            </div>
          ) : (
            <>
              <p className="!t-m-0 t-font-sans t-text-white/60 t-text-sm t-text-center">
                {t(MysteryApplyCode)}
              </p>
              <div
                className="t-w-full t-bg-white/10 t-border t-border-white/20 t-flex t-items-center t-justify-between t-rounded-lg t-cursor-pointer"
                onClick={() => {
                  navigator.clipboard.writeText('GUTHABEN');
                  setCopied(true);
                  setTimeout(() => setCopied(false), 5000);
                }}
              >
                <p className="t-w-[85%] !t-m-0 t-font-sans t-font-bold t-text-lg t-text-center t-text-white t-tracking-widest">
                  GUTHABEN
                </p>
                <div className="t-h-full t-flex t-items-center t-gap-2 t-bg-white/10 t-py-2 t-px-4 t-rounded-[0_8px_8px_0]">
                  {copied ? (
                    <Icon name="tick" className="t-bg-green-500 t-rounded-full t-p-[2px]" />
                  ) : (
                    <img src={CopyToClipboard} alt="" className="t-h-6 t-w-6 t-invert" />
                  )}
                  <p className="!t-m-0 t-text-lg t-font-sans !t-font-bold t-text-white">
                    {copied ? 'Copied' : 'Copy'}
                  </p>
                </div>
              </div>
            </>
          )}
          <CustomLink to={quizLink} className="t-w-full">
            <span className="mutter-cta btn !t-rounded-lg !t-w-full t-font-sans !t-bg-butter !t-text-navy !t-font-bold t-transition-transform t-inline-flex t-items-center t-justify-center t-gap-2">
              {t(RefillBtnText)} →
            </span>
          </CustomLink>
        </div>
      </div>
      <div className="t-flex t-flex-col t-gap-4 t-overflow-auto t-p-4 t-bg-gray-100">
        <h1 className="t-text-[24px] md:t-text-[30px] t-text-center t-font-bold t-mb-0 t-text-tertiary">
          {t(CustomerReviews)}
        </h1>
        <div className="t-w-full t-max-w-[1330px] t-mx-auto">
          <ReviewsMobile />
        </div>
      </div>
    </>
  );
};

export default MysteryCredit;
